import axios from 'axios';
import { defineStore } from 'pinia';
import { AuthService } from '@/api';
import { clearHelpChatStorage } from '@/composables/useHelpChat';
import { router } from '@/router/router';
import { createCookie, eraseCookie, jwt, readCookie } from '@/utils';
import { getTenant } from '@/utils/helpers/urlManipulation';
import { useActivityProjectStore } from '../activityProjectStore';
import { useActivityTemplateStore } from '../activityTemplateStore';
import { useFunctionStore } from '../functionStore';
import { useMilestoneProjectStore } from '../milestoneProjectStore';
import { useMilestoneTemplateStore } from '../milestoneTemplateStore';
import { usePhaseProjectStore } from '../phaseProjectStore';
import { usePhaseTemplateStore } from '../phaseTemplateStore';
import { useProfileStore } from '../profileStore';
import { useProjectStore } from '../projectStore';
import { useMapStore } from '../remapStore';
import { useTemplateStore } from '../templateStore';
import { useUserStore } from '../userStore';
import { entraLogin } from './entraClient';

type IExpire = { exp: number };

const ACCESS = 'access_token';
const REFRESH = 'refresh_token';

const tokenExpiry = (token: string): Date => {
  const { exp } = jwt.decode<IExpire>(token).payload;

  return new Date(exp * 1000);
};

export type AuthStoreState = {
  tokens: {
    access: string;
    refresh: string;
  };
  isAuthenticating: boolean;
};

export const useAuthStore = defineStore('authStore', {
  state: (): AuthStoreState => ({
    tokens: {
      access: readCookie(ACCESS),
      refresh: readCookie(REFRESH),
    },
    isAuthenticating: false,
  }),

  getters: {
    decoded(state) {
      const access = jwt.decode<IExpire>(state.tokens.access);
      const refresh = jwt.decode<IExpire>(state.tokens.refresh);

      return {
        access: access.payload,
        refresh: refresh.payload,
      };
    },

    hasTokens(state) {
      return state.tokens.access !== '' && state.tokens.refresh !== '';
    },

    expirationTime(state) {
      const { exp } = jwt.decode(state.tokens.refresh).payload as IExpire;

      return exp * 1000;
    },

    canRefresh(state) {
      return () => {
        if (!this.hasTokens) {
          return false;
        }
        const { exp } = jwt.decode(state.tokens.refresh).payload as IExpire;

        return exp * 1000 > Date.now();
      };
    },

    validBearer(state) {
      return () => {
        if (!this.hasTokens) {
          return false;
        }
        const { exp } = jwt.decode(state.tokens.access).payload as IExpire;

        return exp * 1000 > Date.now();
      };
    },
  },

  actions: {
    async login(username: string, password: string) {
      try {
        const res = await AuthService.login(username, password);
        this.tokens.access = createCookie(
          ACCESS,
          res.access_token,
          tokenExpiry(res.access_token),
        );
        this.tokens.refresh = createCookie(
          REFRESH,
          res.refresh_token,
          tokenExpiry(res.refresh_token),
        );
        await useProfileStore().loadProfile();

        const redirectPath = router.currentRoute.value.query.redirect as string;
        if (redirectPath && redirectPath !== '/login') {
          await router.push(redirectPath);
        } else {
          await router.push({ name: 'Dashboard' });
        }
      } catch (error) {
        console.error('[AuthStore] "login" action has failed', error);
        throw error;
      }
    },

    async logout() {
      try {
        const refresh_token = this.$state.tokens.refresh;
        const access_token = this.$state.tokens.access;
        await AuthService.logout(refresh_token, access_token);
      } catch (error) {
        console.error('[AuthStore] Error: "logout" action has failed', error);
      } finally {
        this.resetStores();
        clearHelpChatStorage();
        this.tokens.access = eraseCookie(ACCESS);
        this.tokens.refresh = eraseCookie(REFRESH);
        await router.push({ name: 'Login' });
        window.location.reload();
      }
    },

    async ssoLogin() {
      const { VITE_API_ENDPOINT: apiEndpoint } = import.meta.env;
      try {
        const subdomain = getTenant();
        const tenantId = import.meta.env[`VITE_${subdomain}_TENANT_ID`];
        const appId = import.meta.env[`VITE_${subdomain}_CLIENT_ID`];

        // Preserve redirect URL before authentication (before navigating to Microsoft)
        // If we're on /login?redirect=/projects/123, store it for after auth
        const currentRedirect = router.currentRoute.value.query
          .redirect as string;
        if (currentRedirect) {
          sessionStorage.setItem('auth_redirect', currentRedirect);
        }

        const { idToken, accessToken } = await entraLogin(tenantId, appId);

        const res: {
          data: {
            access_token: string;
            refresh_token: string;
          };
        } = await axios.post(`${apiEndpoint}/v1/login-token`, {
          grant_type: 'token',
          access_token: accessToken,
          id_token: idToken,
          tenant_id: tenantId,
          app_id: appId,
        });
        this.tokens.access = createCookie(
          ACCESS,
          res.data.access_token,
          tokenExpiry(res.data.access_token),
        );
        this.tokens.refresh = createCookie(
          REFRESH,
          res.data.refresh_token,
          tokenExpiry(res.data.refresh_token),
        );
        const profileStore = useProfileStore();
        // Fire-and-forget: navigation no longer waits for profile, settings, or
        // photo sync. The dashboard shell renders immediately; widgets subscribe
        // to these stores and update once the data arrives.
        profileStore.loadProfile().catch((error) => {
          console.error('Error loading profile:', error);
        });
        profileStore
          .loadUserSettings(true)
          .then(async () => {
            const { has_profile_picture, profile_picture_is_custom } =
              profileStore.settings;
            if (!profile_picture_is_custom && !has_profile_picture) {
              try {
                await profileStore.syncMsPhoto(accessToken);
              } catch {
                // No MS photo available or silent token failed — nothing to show
              }
            } else {
              await profileStore.loadMyProfilePicture(has_profile_picture);
            }
          })
          .catch((error) => {
            console.error('Error loading user settings:', error);
          });

        // Check for stored redirect first (survives auth flow), then query param
        const storedRedirect = sessionStorage.getItem('auth_redirect');
        const queryRedirect = router.currentRoute.value.query
          .redirect as string;
        const redirectPath = storedRedirect || queryRedirect;

        // Clean up stored redirect
        if (storedRedirect) {
          sessionStorage.removeItem('auth_redirect');
        }

        if (redirectPath && redirectPath !== '/login') {
          await router.push(redirectPath);
        } else {
          await router.push({ name: 'Dashboard' });
        }
      } catch (error) {
        console.error('[AuthStore] "login" action has failed', error);
        // Clean up on error
        sessionStorage.removeItem('auth_redirect');
        throw error;
      }
    },

    async refreshToken() {
      if (!this.canRefresh()) {
        return router.push({ name: 'Login' });
      }

      this.$state.isAuthenticating = true;
      try {
        if (!this.hasTokens) {
          this.$state.isAuthenticating = false;
          await router.push({ name: 'Login' });
          window.location.reload();
        } else {
          const res = await AuthService.refresh(
            this.$state.tokens.refresh as string,
          );
          this.tokens.access = createCookie(
            ACCESS,
            res.access_token,
            tokenExpiry(res.access_token),
          );
          this.tokens.refresh = createCookie(
            REFRESH,
            res.refresh_token,
            tokenExpiry(res.refresh_token),
          );
          this.$state.isAuthenticating = false;
        }
      } catch (error) {
        console.error('[AuthStore] Error: "refresh" action has failed', error);
        this.$state.isAuthenticating = false;
        await router.push({ name: 'Login' });
        clearHelpChatStorage();
        this.tokens.access = eraseCookie(ACCESS);
        this.tokens.refresh = eraseCookie(REFRESH);
        window.location.reload();
      }
    },

    resetStores() {
      useMapStore().$reset();
      useActivityProjectStore().$reset();
      useActivityTemplateStore().$reset();
      useFunctionStore().$reset();
      useMilestoneProjectStore().$reset();
      useMilestoneTemplateStore().$reset();
      usePhaseProjectStore().$reset();
      usePhaseTemplateStore().$reset();
      useProjectStore().$reset();
      useTemplateStore().$reset();
      useProfileStore().$reset();
      useUserStore().revokeAllAvatarUrls();
      useUserStore().$reset();
    },
  },
});
